import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";

import { isDefined } from "Utils/functions";
import { useFormikContext } from "formik";

type GenericFunction<P extends Array<any>> = (...params: P) => any;

export const useDebounce = (
  delay = 300,
): (<P extends Array<any>>(f: GenericFunction<P>, ...params: P) => void) => {
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  return <P extends Array<any>>(f: GenericFunction<P>, ...params: P): void => {
    if (isDefined(timerRef.current)) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      f(...params);
      timerRef.current = null;
    }, delay);
  };
};

type InputChangeEvent = ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;

export const useDebouncedInputField = (
  fieldName: string,
  delay = 1000,
): [
  string,
  (e: InputChangeEvent, onChange?: (value: string) => void) => void,
] => {
  const formikContext = useFormikContext<Record<string, unknown>>();
  const [innerValue, setInnerValue] = useState("");
  const debounce = useDebounce(delay);

  const fieldValue = formikContext.values[fieldName];

  useEffect(() => {
    isDefined(fieldValue) && typeof fieldValue === "string"
      ? setInnerValue(fieldValue)
      : setInnerValue("");
  }, [fieldValue]);

  const handleDebouncedOnChange = useCallback(
    (e: InputChangeEvent, onChange?: (value: string) => void) => {
      const newValue = e.target.value;
      setInnerValue(newValue);
      debounce(() => {
        formikContext.setFieldValue(fieldName, newValue);
        onChange && onChange(newValue);
      });
    },
    [],
  );

  return [innerValue, handleDebouncedOnChange];
};
